import { useState } from "react";
import { Flag } from "lucide-react";
import { Modal } from "@/components/ui/Modal";
import { Button } from "@/components/ui/PageHeader";
import {
  createReport,
  REPORT_REASONS,
  REASON_LABELS,
  type ReportableType,
  type ReportReason,
} from "@/api/reports";

/**
 * The report affordance — small on purpose.
 *
 * A flag, a reason, an optional sentence. It sits quietly on a card because a
 * loud report button changes what people post more than it changes what gets
 * reported. Two behaviours worth knowing when testing:
 *
 * - Submitting twice on the same item UPDATES your report rather than adding a
 *   second one, and the response never says whether others reported it too —
 *   there is nothing to learn by hammering it.
 * - "I'm worried about this person" is a different kind of report from the
 *   rest: it is concern for the author, not an accusation about content. The
 *   copy keeps that distinction.
 */
export function ReportButton({
  contentType,
  contentId,
}: {
  contentType: ReportableType;
  contentId: string;
}) {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState<ReportReason | null>(null);
  const [details, setDetails] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit() {
    if (!reason) return;
    setBusy(true);
    setError(null);
    try {
      await createReport({
        contentType,
        contentId,
        reason,
        details: details.trim() || undefined,
      });
      setDone(true);
      setOpen(false);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Report failed");
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        className={`inline-flex items-center gap-1 text-xs transition-colors ${
          done ? "text-gray-400" : "text-gray-400 hover:text-red-600"
        }`}
        title={done ? "Reported — submitting again updates your report" : "Report"}
      >
        <Flag className="w-3 h-3" />
        {done ? "Reported" : "Report"}
      </button>

      <Modal
        isOpen={open}
        onClose={() => setOpen(false)}
        title="Report this"
        size="sm"
        footer={
          <div className="flex items-center justify-end gap-2">
            <Button variant="ghost" size="sm" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button size="sm" onClick={submit} disabled={!reason} isLoading={busy}>
              Send report
            </Button>
          </div>
        }
      >
        <div className="space-y-1.5">
          {REPORT_REASONS.map((r) => (
            <label
              key={r}
              className={`flex items-center gap-2.5 rounded-lg border px-3 py-2 text-sm cursor-pointer transition-colors ${
                reason === r
                  ? "border-gray-900 bg-gray-50"
                  : "border-gray-200 hover:border-gray-300"
              }`}
            >
              <input
                type="radio"
                name="report-reason"
                checked={reason === r}
                onChange={() => setReason(r)}
                className="h-3.5 w-3.5"
              />
              <span className="text-gray-800">{REASON_LABELS[r]}</span>
            </label>
          ))}
        </div>

        <textarea
          value={details}
          onChange={(e) => setDetails(e.target.value)}
          rows={2}
          maxLength={2000}
          placeholder="Anything the reviewer should know (optional)"
          className="mt-3 w-full rounded-lg border border-gray-200 p-2.5 text-sm focus:border-gray-400 focus:outline-none"
        />

        {reason === "self_harm_concern" && (
          <p className="mt-2 rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-900">
            This flags the post for a human with concern for the person, not as
            an accusation. Thank you for saying something.
          </p>
        )}

        {error && <p className="mt-2 text-xs text-red-600">{error}</p>}
      </Modal>
    </>
  );
}
